// Pure animation helpers for the scene (no three.js import): easings, damping, a tiny tween
// scheduler and a 2-bone IK solver used for the agents' arms.

export const ease = Object.freeze({
  linear: (t) => t,
  inOutCubic: (t) => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2),
  outCubic: (t) => 1 - (1 - t) ** 3,
  inCubic: (t) => t * t * t,
  outBack: (t) => {
    const c1 = 1.70158;
    const c3 = c1 + 1;
    return 1 + c3 * (t - 1) ** 3 + c1 * (t - 1) ** 2;
  },
  inOutSine: (t) => -(Math.cos(Math.PI * t) - 1) / 2,
  outQuint: (t) => 1 - (1 - t) ** 5,
});

/** Clamps v to [a, b]. */
export function clamp(v, a, b) {
  return v < a ? a : v > b ? b : v;
}

/** Linear interpolation. */
export function lerp(a, b, t) {
  return a + (b - a) * t;
}

/**
 * Frame-rate independent exponential approach of `current` toward `target`.
 * @param {number} current
 * @param {number} target
 * @param {number} rate  higher = faster (1/s)
 * @param {number} dt    seconds
 */
export function damp(current, target, rate, dt) {
  return lerp(current, target, 1 - Math.exp(-rate * dt));
}

/** Wraps an angle to (-π, π]. */
export function wrapAngle(a) {
  let x = a % (Math.PI * 2);
  if (x > Math.PI) x -= Math.PI * 2;
  if (x <= -Math.PI) x += Math.PI * 2;
  return x;
}

/** Damps an angle along the shortest arc. */
export function dampAngle(current, target, rate, dt) {
  return current + wrapAngle(target - current) * (1 - Math.exp(-rate * dt));
}

/**
 * Two-bone IK in a vertical plane (y up, z forward). A limb with pitch `a` points along
 * (y, z) = (-cos a, -sin a): pitch 0 hangs straight down, pitch -π/2 points forward.
 * The elbow bends downward/backward (natural for arms reaching forward).
 * @param {number} sy shoulder y
 * @param {number} sz shoulder z
 * @param {number} ty target y
 * @param {number} tz target z
 * @param {number} l1 upper limb length
 * @param {number} l2 lower limb length
 * @returns {{a1:number, a2:number}} a1 = upper pitch, a2 = lower pitch relative to the upper limb
 */
export function solveTwoBone(sy, sz, ty, tz, l1, l2) {
  const dy = ty - sy;
  const dz = tz - sz;
  const d = clamp(Math.hypot(dy, dz), Math.abs(l1 - l2) + 1e-4, l1 + l2 - 1e-4);
  const base = Math.atan2(-dz, -dy);
  const cosA = clamp((l1 * l1 + d * d - l2 * l2) / (2 * l1 * d), -1, 1);
  const a1 = base + Math.acos(cosA);
  const ey = sy - Math.cos(a1) * l1;
  const ez = sz - Math.sin(a1) * l1;
  // Direction from the elbow to the (reachable) target point.
  const k = d / Math.max(Math.hypot(dy, dz), 1e-6);
  const px = sy + dy * k;
  const pz = sz + dz * k;
  const b = Math.atan2(-(pz - ez), -(px - ey));
  return { a1: wrapAngle(a1), a2: wrapAngle(b - a1) };
}

/** Longest first step of a new tween (ms): about one frame at 60 fps. */
export const FIRST_STEP_MS = 17;

/**
 * Tween scheduler driven by the scene clock. Each tween interpolates from 0 to 1 over `duration`
 * milliseconds and calls `onUpdate(easedT, rawT)`; `onDone` fires once at the end. Duration 0
 * applies the end state at the next `update`. The scene feeds it wall-clock time, so a transition
 * lasts its duration even when frames are slow; the first step of a new tween counts at most one
 * frame (FIRST_STEP_MS), since the time before the tween was added is not its own.
 */
export class Tweens {
  constructor() {
    this.list = [];
  }

  /**
   * @param {{duration:number, ease?:(t:number)=>number, onUpdate:(t:number)=>void, onDone?:()=>void, key?:string}} opts
   * A tween with the same `key` as a running one replaces it (the old one does not complete).
   */
  add({ duration, ease: fn = ease.inOutCubic, onUpdate, onDone, key }) {
    if (key) this.list = this.list.filter((t) => t.key !== key);
    const t = { duration: Math.max(0, duration), ease: fn, onUpdate, onDone, key, elapsed: 0, fresh: true };
    this.list.push(t);
    return t;
  }

  /** Advances every tween by `dtMs`; returns true while at least one is running. */
  update(dtMs) {
    if (!this.list.length) return false;
    const done = [];
    for (const t of [...this.list]) {
      t.elapsed += t.fresh ? Math.min(dtMs, FIRST_STEP_MS) : dtMs;
      t.fresh = false;
      const p = t.duration === 0 ? 1 : Math.min(1, t.elapsed / t.duration);
      t.onUpdate?.(t.ease(p), p);
      if (p >= 1) done.push(t);
    }
    if (done.length) {
      this.list = this.list.filter((t) => !done.includes(t));
      for (const t of done) t.onDone?.();
    }
    return this.list.length > 0;
  }

  /** Stops a running tween without completing it. */
  cancel(key) {
    this.list = this.list.filter((t) => t.key !== key);
  }

  /** True when a tween with this key is running. */
  has(key) {
    return this.list.some((t) => t.key === key);
  }

  get active() {
    return this.list.length > 0;
  }

  clear() {
    this.list = [];
  }
}
